// The product's single configured dayjs instance. `utc` — which `formatDate` needs
// for its calendar-date branch — is registered there along with the rest of the
// plugin set. This module used to import 'dayjs' and call `extend(utc)` for itself,
// which is precisely the failure mode one config removes: whether `.utc()` existed
// depended on which config had happened to load first.
import dayjs from './dayjs.ts'

/**
 * Date formatting strategy across the project.
 *
 * The project intentionally treats dates differently in three layers with
 * three different invariants — they share a wire format (`YYYY-MM-DD`) but
 * cannot share an implementation.
 *
 *   1. UI display (this module — `formatDate` / `formatDateTime`)
 *      Input  : `string | Date | null | undefined` from the React layer
 *                — typically a `DATE` column value, a `Date` object from a
 *                date-picker, or a `TIMESTAMPTZ` ISO string already
 *                serialised by Supabase.
 *      Output : `MM/DD/YYYY` (date) or `MM/DD/YYYY, h:mm:ss A` (date-time),
 *                rendered in the viewer's local timezone. This is the right
 *                behaviour for "when did it happen" timestamps — a user in
 *                EST viewing a 15:30 UTC `last_sync_at` expects to see
 *                "10:30 AM" in their wall-clock.
 *      Caveat : do not feed a bare ISO instant string with a "Z" suffix
 *                here when you mean a *calendar date*. The display is
 *                tz-shifted to local, which is the intended semantics for
 *                timestamps but wrong for calendar dates. Pass the
 *                `YYYY-MM-DD` part only in that case.
 *
 *   2. ETL parsing (`packages/tasks/src/lib/import-utils.ts` —
 *      `formatDateLocal`) Input is CSV / XLSX user data; output is the
 *      canonical `YYYY-MM-DD` written to a `DATE` column. Parses string
 *      inputs as UTC (`dayjs.utc`) so a CSV date doesn't roll back a day
 *      when round-tripped through `new Date(...)` in a negative-offset
 *      timezone.
 *
 *   3. DB query bounds (`apps/web/src/lib/db-tenant.ts`) — no Date helper.
 *      The UI converts the picked calendar day to a `YYYY-MM-DD` string at
 *      the edge, and that string reaches `.gte()` / `.lte()` untouched:
 *      formatting the picker's local-midnight `Date` in UTC shifted it a day
 *      for viewers east of Greenwich. "Today" comes from `businessDate()`.
 *
 * If you need another date function, ask whether your layer's input
 * shape and timezone semantics actually match one of the two helpers above
 * (or the string-only boundary of layer 3) before introducing one.
 */

/**
 * BCP 47 locale pinned for every display-side number / currency / date
 * format in DCP v1. The product currently supports USD-only tenants, so
 * mixing the viewer's browser locale into formatted output would make
 * the same dollar amount render differently for two users staring at
 * the same screen (e.g. `1,234.56` vs `1.234,56`) — observed bug in
 * early DCP screenshots.
 *
 * Single source of truth so the future i18n epic (org-level locale
 * column on `organizations` + a `useLocale()` hook) has exactly one
 * import to flip. Every `toLocaleString` / `Intl.NumberFormat` call in
 * the import details modals and any future per-row formatter must
 * consume this constant rather than re-literal-ing `'en-US'`.
 *
 * @see TransactionImportDetailsModal — uses this in `formatAmount`.
 * @see ProjectionImportDetailsModal — uses this in `formatProjectionAmount`.
 */
export const DISPLAY_LOCALE = 'en-US' as const

/**
 * Options for formatting currency
 */
export interface FormatCurrencyOptions {
  /** Number of decimal places (default: 2) */
  decimals?: number
  /** How to display negative numbers (default: 'minus') */
  negativeStyle?: 'minus' | 'parentheses'
  /** ISO 4217 currency code (default: 'USD') */
  currency?: string
  /** BCP 47 locale string (default: 'en-US') */
  locale?: string
}

/**
 * Formats a number as currency string
 * @param amount - The numeric amount to format
 * @param options - Optional formatting configuration
 * @returns Formatted currency string (e.g., "$1,234.56")
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(-1234.56, { negativeStyle: 'parentheses', decimals: 0 }) // "($1,235)"
 * formatCurrency(1234, { decimals: 0 }) // "$1,234"
 */
export function formatCurrency(
  amount: number,
  options: FormatCurrencyOptions = {}
): string {
  const {
    decimals = 2,
    negativeStyle = 'minus',
    currency = 'USD',
    locale = 'en-US',
  } = options

  // Normalise negative zero. `Intl` treats -0 as negative and renders it as
  // "-$0.00" / "($0.00)" — a zero that reads as money out. JS produces -0 from
  // ordinary arithmetic (`-outstandingAmount` for an empty Outstanding Checks
  // tile, `-tx.amount` for a zero-amount Plaid row), so this is reachable, and
  // in a figure the Touch Support team retypes into Sage a spurious minus is
  // not cosmetic. `Object.is` is deliberate: it matches ONLY exact -0 and
  // leaves a genuine small negative such as -0.004 — which legitimately rounds
  // to a negative zero display — alone.
  const normalised = Object.is(amount, -0) ? 0 : amount

  // Use Intl.NumberFormat with currencySign: 'accounting' for parentheses style
  // This handles all currencies properly without hardcoding currency symbols
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    currencySign: negativeStyle === 'parentheses' ? 'accounting' : 'standard',
  }).format(normalised)

  return formatted
}

/**
 * Returns the stored signed amount unchanged.
 *
 * Phase 1 of DCP-250 backfilled all `transactions.amount` values to carry
 * their display sign directly (positive = inflow, negative = outflow), so the
 * stored value is already the correct display value. This function now acts as
 * a pass-through.
 *
 * Call sites that still pass `categoryType` are Phase-3 cleanup candidates —
 * the parameter is accepted here only for backward call-site compatibility
 * (prevents compilation breakage before Phase 3 cleans them up).
 *
 * @param amount - The stored signed numeric amount (positive = inflow, negative = outflow)
 * @param categoryType - @deprecated IGNORED. The sign is now derived from the
 *   amount value itself. Callers should migrate to omitting this argument in
 *   Phase 3.
 * @returns The `amount` unchanged
 */
export function signedAmountByCategory(
  amount: number,
  categoryType?: 'inflow' | 'outflow' | null | undefined,
): number {
  // categoryType is intentionally unused: the stored amount is already signed
  // after the Phase-1 DCP-250 migration backfill.
  void categoryType
  return amount
}

/**
 * Returns true when an amount should be rendered as a negative value
 * (parentheses + red colour) at the display layer.
 *
 * After the Phase-1 DCP-250 migration the stored amount is already signed
 * (negative = outflow, positive = inflow), so the sign is read directly from
 * the value. `amount < 0` is the sole source of truth.
 *
 * Note on `-0`: `(-0) < 0` is `false` in IEEE 754, which is the desired
 * behaviour — a zero amount is neither positive nor negative and should not
 * be coloured red.
 *
 * @param amount - The stored signed numeric amount (positive = inflow, negative = outflow)
 * @param categoryType - @deprecated IGNORED. The sign is now derived from the
 *   amount value itself. Callers should migrate to omitting this argument in
 *   Phase 3.
 * @returns true if `amount < 0` (the row should display as negative: red + parentheses)
 */
export function isAmountNegativeByCategory(
  amount: number,
  categoryType?: 'inflow' | 'outflow' | null | undefined,
): boolean {
  // categoryType is intentionally unused: the stored amount is already signed
  // after the Phase-1 DCP-250 migration backfill.
  void categoryType
  return amount < 0
}

/**
 * Single source of truth for rendering a signed transaction amount in the UI:
 * returns the formatted `text` (parentheses style for negatives, e.g.
 * `($1,234.56)`) and the Tailwind colour `className` (`text-green-600` for
 * positive / `text-red-600` for negative) driven entirely by the value's sign.
 *
 * After the Phase-1 DCP-250 migration the stored `amount` is already signed
 * (positive = inflow, negative = outflow), so no category-type flip is needed.
 * The `categoryType` parameter is retained only for backward call-site
 * compatibility — it is forwarded to the now-ignored helpers and will be
 * cleaned up in Phase 3.
 *
 * Locale and parentheses-style formatting are routed through `formatCurrency`
 * so callers automatically pick up any future Intl / locale change. Currency
 * is intentionally not parameterised — DCP v1 is USD-only.
 *
 * @param amount - Stored signed amount (positive = inflow, negative = outflow).
 * @param categoryType - @deprecated IGNORED. The sign is now derived from the
 *   amount value itself. Callers should migrate to omitting this argument in
 *   Phase 3.
 * @returns Object with the rendered `text` and Tailwind colour `className`
 *          ready to spread onto a span.
 *
 * @example
 * formatCategoryAmount(1234.56)
 * // → { text: '$1,234.56', className: 'text-green-600' }
 * formatCategoryAmount(-1234.56)
 * // → { text: '($1,234.56)', className: 'text-red-600' }
 * formatCategoryAmount(-100, null)
 * // → { text: '($100.00)', className: 'text-red-600' }
 */
export function formatCategoryAmount(
  amount: number,
  categoryType?: 'inflow' | 'outflow' | null | undefined,
  options?: { decimals?: number },
): { text: string; className: string } {
  // signedAmountByCategory and isAmountNegativeByCategory both ignore
  // categoryType after the Phase-1 DCP-250 backfill — the stored value is
  // already signed. The categoryType arg is forwarded only so existing callers
  // keep compiling without changes in Phase 3.
  const displayAmount = signedAmountByCategory(amount, categoryType)
  const isNegative = isAmountNegativeByCategory(amount, categoryType)
  const text = formatCurrency(displayAmount, {
    // Cents by default. The override exists for a caller that wants a
    // whole-dollar display while keeping the shared sign/colour logic; since
    // DCP-392 no caller uses it (the Daily Banking table was the last one —
    // Touch Support needs exact figures to post to Sage Intacct).
    decimals: options?.decimals ?? 2,
    negativeStyle: 'parentheses',
  })
  // A zero amount has no flow direction, so it must not read as an inflow:
  // render it in the neutral muted colour rather than the green "money-in"
  // colour. Negatives are red, positives green. This keeps zero consistent
  // with the projection/import tables, which already render zero as muted.
  const className = amount === 0 ? 'text-muted-foreground' : isNegative ? 'text-red-600' : 'text-green-600'
  return {
    text,
    className,
  }
}

/**
 * Matches a bare YYYY-MM-DD calendar-date string (no time component, no
 * timezone). Used to detect DATE-column values so they can be parsed in
 * UTC and stay stable across viewer timezones — anything that isn't this
 * exact shape (Date objects, ISO timestamps with T/Z, mixed formats)
 * falls through to the local-tz path that TIMESTAMPTZ display needs.
 *
 * The check is shape-only — semantic validity (real calendar month/day)
 * is delegated to `dayjs.isValid()` downstream.
 */
const CALENDAR_DATE_STRING_RE = /^\d{4}-\d{2}-\d{2}$/

/**
 * Formats a date value as MM/DD/YYYY (banking convention).
 *
 * Type-aware parse:
 *   - `YYYY-MM-DD` string from a `DATE` column → parsed with `dayjs.utc`
 *     so the calendar day stays stable across viewer timezones (no roll
 *     to previous day in negative-offset TZs).
 *   - `Date` from a date-picker → parsed with `dayjs(date)` (local).
 *   - `TIMESTAMPTZ` ISO string from Supabase → parsed with `dayjs(date)`
 *     (local) — viewer's wall-clock is the right semantics for "when did
 *     it happen" timestamps. See module header for the rationale.
 *
 * Avoid feeding a bare instant string like `"2024-01-15T00:00:00Z"` when
 * you mean a *calendar date* — that input falls into the TIMESTAMPTZ
 * branch and can land on the previous day in negative-offset timezones.
 * Strip the time component to `YYYY-MM-DD` first, or use `formatDateLocal`
 * (ETL), which is explicitly UTC.
 *
 * Returns `'-'` for null, undefined, or unparseable input instead of throwing.
 *
 * @param date - Date object, ISO string, or null/undefined
 * @returns Formatted date string (e.g., "01/15/2024") or '-'
 */
export function formatDate(date: string | Date | null | undefined): string {
  if (date == null) return '-'
  // Discriminate DATE-column strings from everything else so calendar
  // dates render the same day regardless of viewer timezone.
  const isCalendarDateString =
    typeof date === 'string' && CALENDAR_DATE_STRING_RE.test(date)
  const d = isCalendarDateString ? dayjs.utc(date) : dayjs(date)
  if (!d.isValid()) return '-'
  return d.format('MM/DD/YYYY')
}

/**
 * Formats a date-time value as MM/DD/YYYY, h:mm:ss A (banking convention).
 *
 * Intended for `TIMESTAMPTZ` values ("when did it happen?" — sync runs,
 * created_at, etc). Output is the viewer's local wall-clock time, which is
 * the right semantics for timestamps. Do not pass calendar-date strings
 * here unless you specifically want the local midnight rendering.
 *
 * Returns `'-'` for null, undefined, or unparseable input instead of throwing.
 *
 * @param date - Date object, ISO string, or null/undefined
 * @returns Formatted date-time string (e.g., "01/15/2024, 2:30:00 PM") or '-'
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  if (date == null) return '-'
  const d = dayjs(date)
  if (!d.isValid()) return '-'
  return d.format('MM/DD/YYYY, h:mm:ss A')
}

/**
 * Formats a number using Intl.NumberFormat
 * @param num - Number to format
 * @param options - Intl.NumberFormatOptions for customization
 * @param locale - BCP 47 locale string (default: 'en-US')
 * @returns Formatted number string (e.g., "1,234.56")
 */
export function formatNumber(
  num: number,
  options: Intl.NumberFormatOptions = {},
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, options).format(num)
}

/**
 * Truncates a string to specified length with ellipsis
 * @param str - String to truncate
 * @param length - Maximum length before truncation
 * @returns Truncated string with "..." suffix if exceeded length
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return `${str.slice(0, length)}...`
}